import React, { useContext } from 'react';
import { SloDTO, SloDTOFromJSON } from '@atlassiansox/phobos-client-ts';
import { useQuery } from '@tanstack/react-query';

import {
  sortSlos,
  useRefreshController,
  filterStore,
  getSLOFilterQuery,
  useFetchErrorController,
} from '@/controllers';
import { spaceTopCozy } from '@/styles';
import { apiFetch } from '@/services';
import { SLOCardViewType, SearchConfigContext } from '@/controllers/search-config-controller';
import { FCC } from '@/utils/types';

import MiniSLOCard from '../../slo-card/mini-slo-cards/mini';
import DraftStatusLozenge from '../../slo-card/lozenges/draft-status-lozenge';
import SearchPageSkeleton from '../../search/search-results/search-page-skeleton';

import * as styles from './styles';

const MiniSlosEmpty: FCC = ({ children }) => (
  <span css={styles.miniSlosGridEmpty}>
    No&nbsp;<strong>SLO</strong>s to show.
    {children}
  </span>
);

type MiniSlosProps = {
  id: string;
  isSLOCardListView?: boolean;
};

const MiniSlosContainer: React.FC<MiniSlosProps> = ({ id, isSLOCardListView }) => {
  const refreshController = useRefreshController();

  const { state: filterStoreState } = React.useContext(filterStore);

  const sloFilterQuery = getSLOFilterQuery(filterStoreState.slo);
  const ENDPOINT = `/capability/${id}/slos${refreshController.getQuery(
    'graph=spark&size=2000' /* NOTE: page size of 2000 seems to be the cap Phobos sets */,
    'slo'
  )}${sloFilterQuery}`;
  const { onError } = useFetchErrorController();
  const { data, isInitialLoading: isLoading } = useQuery(
    ['capability', 'slos-spark', id, sloFilterQuery],
    () => apiFetch(ENDPOINT),
    { onError }
  );

  const slos: SloDTO[] = (data?.content ?? []).map(SloDTOFromJSON).sort(sortSlos);
  if (isLoading) {
    return (
      <div css={styles.sloCardsGridLoadingStyles}>
        <SearchPageSkeleton isCompact isExtending />
      </div>
    );
  }

  if (!isLoading && slos.length === 0) {
    const hasHiddenDraftSLOs = !filterStoreState.slo.showDrafts;

    if (hasHiddenDraftSLOs) {
      return (
        <MiniSlosEmpty>
          Hiding&nbsp;
          <DraftStatusLozenge />
          &nbsp;<strong>SLO</strong>s
        </MiniSlosEmpty>
      );
    }

    return <MiniSlosEmpty />;
  }

  if (!isLoading && slos.length > 0) {
    return (
      <>
        {slos.map(slo => (
          <React.Fragment key={slo.publicId}>
            {/* eslint-disable react/jsx-props-no-spreading */}
            <MiniSLOCard
              {...slo}
              capabilityId={id}
              isCompact={!isSLOCardListView}
              isExtending={isSLOCardListView}
            />
          </React.Fragment>
        ))}
      </>
    );
  }

  return null;
};

export const MiniSlos: React.FC<MiniSlosProps> = ({ id }) => {
  const { sloCardViewType } = useContext(SearchConfigContext);
  const isSLOCardListView: boolean = sloCardViewType === SLOCardViewType.ListView;

  return (
    <div css={[spaceTopCozy, styles.miniSlosStyle]} data-testid="mini-slos">
      <div css={[styles.miniSlosGrid, isSLOCardListView ? styles.sloCardsGridExtendingStyles : '']}>
        <MiniSlosContainer isSLOCardListView={isSLOCardListView} id={id} />
      </div>
    </div>
  );
};

export default MiniSlos;
